import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "helpers/google_maps_loader"
import { clusterByDistance, centroid } from "helpers/map_clustering"

const IDLE_DEBOUNCE_MS = 250
const CLUSTER_RADIUS = 60
const MIN_ZOOM = 2
const PIN_COLOR = "hsl(228, 45%, 45%)"
const SELECTED_PIN_COLOR = "var(--altrio-red)"

export default class OpportunitiesMapController extends Controller {
  static targets = ["map", "form", "polygonInput"]

  static values = {
    locations: Array,
    bounds: Object,
    mapId: String,
    apiKey: String,
  }

  markers = new Map()

  async connect() {
    await this.#loadGoogleMaps()
    this.#createMap()
    this.suppressNextIdle = true
    this.idleListener = this.map.addListener("idle", () => this.#onIdle())

    if (this.hasBoundsValue) {
      this.map.fitBounds(this.boundsValue, 0)
    } else {
      this.#fitToLocations()
    }
  }

  locationsValueChanged() {
    if (!this.map) return

    this.#renderWhenReady()
    if (!this.hasBoundsValue) this.#fitToLocations()
  }

  #renderWhenReady() {
    if (this.map.getProjection()) {
      this.#renderClusters()
    } else {
      window.google.maps.event.addListenerOnce(
        this.map,
        "projection_changed",
        () => {
          if (this.map) this.#renderClusters()
        }
      )
    }
  }

  focusListing({ detail: { id } }) {
    if (!this.map) return

    const points = this.locationsValue.filter(
      (location) => location.card_id === id
    )
    if (points.length === 0) return

    this.#selectCard(id)
    this.map.panTo(centroid(points))
  }

  disconnect() {
    clearTimeout(this.idleTimeout)
    this.idleListener?.remove()
    this.#removeMarkers()
    this.map = null
  }

  async #loadGoogleMaps() {
    await loadGoogleMaps(this.apiKeyValue)
    const [{ Map }, { AdvancedMarkerElement, PinElement }] = await Promise.all([
      window.google.maps.importLibrary("maps"),
      window.google.maps.importLibrary("marker"),
    ])
    this.mapClass = Map
    this.markerClass = AdvancedMarkerElement
    this.pinClass = PinElement
  }

  #createMap() {
    this.map = new this.mapClass(this.mapTarget, {
      center: { lat: 0, lng: 0 },
      zoom: 1,
      minZoom: MIN_ZOOM,
      disableDefaultUI: true,
      clickableIcons: false,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      keyboardShortcuts: false,
      gestureHandling: "greedy",
      mapId: this.mapIdValue,
    })
  }

  #renderClusters() {
    const projection = this.map.getProjection()
    if (!projection) return

    this.renderedZoom = this.map.getZoom()

    const previous = this.markers
    this.markers = new Map()

    this.#clusterLocations(projection).forEach((cluster) => {
      const key = this.#clusterKey(cluster)
      const marker = previous.get(key) ?? this.#createMarker(cluster)
      marker.cardIds = cluster.locations.map((location) => location.card_id)
      previous.delete(key)
      this.markers.set(key, marker)
    })

    previous.forEach((marker) => (marker.map = null))
    this.#applyMarkerColors()
  }

  #selectCard(id) {
    this.selectedCardId = id
    this.#applyMarkerColors()
  }

  #applyMarkerColors() {
    this.markers.forEach((marker) => {
      const selected = marker.cardIds.includes(this.selectedCardId)
      if (marker.pin) {
        const color = selected ? SELECTED_PIN_COLOR : PIN_COLOR
        marker.pin.background = color
        marker.pin.borderColor = color
      } else {
        marker.content.style.backgroundColor = selected
          ? SELECTED_PIN_COLOR
          : ""
      }
    })
  }

  #clusterKey(cluster) {
    const cardIds = cluster.locations
      .map((location) => location.card_id)
      .sort()
      .join(":")
    return `${cardIds}@${cluster.position.lat},${cluster.position.lng}`
  }

  #createMarker(cluster) {
    return cluster.locations.length === 1
      ? this.#createPin(cluster.locations[0])
      : this.#createClusterMarker(cluster)
  }

  #clusterLocations(projection) {
    const scale = 2 ** this.map.getZoom()
    const points = this.locationsValue.map((location) => ({
      location,
      ...this.#toPixel(projection, location, scale),
    }))

    return clusterByDistance(points, CLUSTER_RADIUS).map((members) => {
      const locations = members.map((member) => member.location)
      return { locations, position: centroid(locations) }
    })
  }

  #toPixel(projection, location, scale) {
    const worldPoint = projection.fromLatLngToPoint(
      new window.google.maps.LatLng(location.lat, location.lng)
    )
    return { x: worldPoint.x * scale, y: worldPoint.y * scale }
  }

  #createPin(location) {
    const pin = new this.pinClass({
      background: PIN_COLOR,
      borderColor: PIN_COLOR,
      glyphColor: "white",
    })

    const marker = new this.markerClass({
      position: { lat: location.lat, lng: location.lng },
      map: this.map,
      content: pin.element,
    })
    marker.pin = pin
    marker.element.addEventListener("click", () => {
      this.#selectCard(location.card_id)
      this.dispatch("markerClick", {
        detail: { id: location.card_id, url: location.pin_card_url },
      })
    })
    return marker
  }

  #createClusterMarker(cluster) {
    const content = document.createElement("div")
    content.className =
      "flex size-8 items-center justify-center rounded-full bg-primary-4 text-xs font-medium text-white shadow ring-2 ring-white cursor-pointer"
    content.textContent = cluster.locations.length

    const marker = new this.markerClass({
      position: cluster.position,
      map: this.map,
      content,
    })
    marker.element.addEventListener("click", () => this.#zoomToCluster(cluster))
    return marker
  }

  #zoomToCluster(cluster) {
    this.map.fitBounds(this.#boundsFor(cluster.locations))
  }

  #boundsFor(locations) {
    const bounds = new window.google.maps.LatLngBounds()
    locations.forEach(({ lat, lng }) => bounds.extend({ lat, lng }))
    return bounds
  }

  #onIdle() {
    if (this.map.getZoom() !== this.renderedZoom) this.#renderClusters()

    if (this.suppressNextIdle) {
      this.suppressNextIdle = false
      return
    }

    clearTimeout(this.idleTimeout)
    this.idleTimeout = setTimeout(
      () => this.#submitCurrentBounds(),
      IDLE_DEBOUNCE_MS
    )
  }

  #submitCurrentBounds() {
    this.polygonInputTarget.value = JSON.stringify(
      this.#boundsToPolygonGeoJSON(this.map.getBounds())
    )
    this.formTarget.requestSubmit()
  }

  #boundsToPolygonGeoJSON(bounds) {
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const south = sw.lat()
    const north = ne.lat()
    const crossesAntimeridian = ne.lng() < sw.lng()

    if (crossesAntimeridian) {
      return {
        type: "MultiPolygon",
        coordinates: [
          [this.#boxRing(sw.lng(), 180, south, north)],
          [this.#boxRing(-180, ne.lng(), south, north)],
        ],
      }
    }

    return {
      type: "Polygon",
      coordinates: [this.#boxRing(sw.lng(), ne.lng(), south, north)],
    }
  }

  #boxRing(west, east, south, north) {
    return [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
      [west, south],
    ]
  }

  #fitToLocations() {
    if (this.locationsValue.length === 0) return

    this.suppressNextIdle = true
    this.map.fitBounds(this.#boundsFor(this.locationsValue))
  }

  #removeMarkers() {
    this.markers.forEach((marker) => (marker.map = null))
    this.markers = new Map()
  }
}
